import moment from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';

import classNames from './styles.module.scss';

// ─── Dot / badge color from days-until ───────────────────────────────────────

const getItemUrgency = dateStr => {
  const d = moment(dateStr, 'MMM D, YYYY');
  if (!d.isValid()) return 'future';

  const daysUntil = d.startOf('day').diff(moment().startOf('day'), 'days');
  if (daysUntil < 0) return 'past';
  if (daysUntil === 0) return 'today';
  if (daysUntil <= 3) return 'warning';
  if (daysUntil <= 7) return 'safe';
  return 'future';
};

const URGENCY_DOT_COLOR = {
  past: '#CBD5E1',
  today: '#DC2626',
  warning: '#FBBF24',
  safe: '#14B8A6',
  future: '#CBD5E1',
};

const URGENCY_BADGE_CLASS = {
  past: 'badgePast',
  today: 'badgeToday',
  warning: 'badgeWarning',
  safe: 'badgeSafe',
  future: 'badgeFuture',
};

const URGENCY_BADGE_LABEL = {
  past: 'Passed',
  today: 'Due Today',
  warning: days => `${days} day${days === 1 ? '' : 's'} left`,
  safe: days => `${days} days away`,
  future: days => `${days} days away`,
};

const getBadgeLabel = (urgency, dateStr) => {
  if (urgency === 'past' || urgency === 'today') return URGENCY_BADGE_LABEL[urgency];

  const d = moment(dateStr, 'MMM D, YYYY');
  const daysUntil = d.startOf('day').diff(moment().startOf('day'), 'days');

  const labelFn = URGENCY_BADGE_LABEL[urgency];
  return typeof labelFn === 'function' ? labelFn(daysUntil) : labelFn;
};

// ─── Single deadline row ──────────────────────────────────────────────────────

const DeadlineRow = ({ item, innerRef }) => {
  const urgency = getItemUrgency(item.date);
  const dotColor = URGENCY_DOT_COLOR[urgency];
  const badgeClass = URGENCY_BADGE_CLASS[urgency];
  const badgeLabel = getBadgeLabel(urgency, item.date);

  return (
    <div ref={innerRef} className={classNames.periodRow}>
      <div className={classNames.periodDot} style={{ background: dotColor }} />
      <div className={classNames.periodInfo}>
        <div className={classNames.periodName}>{item.label}</div>
        <div className={classNames.periodDate}>{item.date}</div>
      </div>
      <span className={[classNames.periodBadge, classNames[badgeClass]].join(' ')}>
        {badgeLabel}
      </span>
    </div>
  );
};

// ─── Period group block ───────────────────────────────────────────────────────

const PeriodGroupBlock = ({ t, group, isCurrentPeriod, scrollTargetId, scrollTargetRef }) => (
  <div className={classNames.upcomingPeriods}>
    <div className={classNames.periodGroupHeader}>
      <span>
        {group.monthLabel}
        {isCurrentPeriod && (
          <span className={classNames.currentPeriodInlineTag}> · {t('currentPeriod')}</span>
        )}
      </span>
    </div>
    <div className={classNames.periodRowsScroll}>
      {group.items.map(item => (
        <DeadlineRow
          key={item.id}
          item={item}
          innerRef={item.id === scrollTargetId ? scrollTargetRef : null}
        />
      ))}
    </div>
  </div>
);

// ─── UpcomingDeadlines ────────────────────────────────────────────────────────

const UpcomingDeadlines = ({ t, previous, current, next }) => {
  const [activeTab, setActiveTab] = useState('current');
  const scrollTargetRef = useRef(null);

  const tabs = [
    { key: 'previous', label: `← ${t('previous')}`, groups: previous },
    { key: 'current', label: t('current'), groups: current },
    { key: 'next', label: `${t('next')} →`, groups: next },
  ];

  const activeGroups = tabs.find(tab => tab.key === activeTab)?.groups || [];

  let firstNonPastId = null;
  for (const group of activeGroups) {
    for (const item of group.items) {
      if (getItemUrgency(item.date) !== 'past') {
        firstNonPastId = item.id;
        break;
      }
    }
    if (firstNonPastId !== null) break;
  }

  useEffect(() => {
    const el = scrollTargetRef.current;
    if (el) {
      const container = el.parentElement;
      container.scrollTop =
        el.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
    }
  }, [current, activeTab]);

  return (
    <>
      <div className={classNames.periodTabsRow}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            className={[
              classNames.periodTab,
              activeTab === tab.key ? classNames.periodTabActive : '',
            ].join(' ')}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeGroups.length === 0 ? (
        <div className={classNames.emptyPanel}>{t('noDeadlinesToDisplay')}</div>
      ) : (
        activeGroups.map((group, idx) => (
          <PeriodGroupBlock
            key={group.monthKey}
            t={t}
            group={group}
            isCurrentPeriod={activeTab === 'current' && idx === 0}
            scrollTargetId={firstNonPastId}
            scrollTargetRef={scrollTargetRef}
          />
        ))
      )}
    </>
  );
};

export default UpcomingDeadlines;
